import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const EMPTY_LINE = { product_id: "", product_name: "", quantity: "", rate: "" };

export default function VendorPurchaseOrders() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ buyer_name: "", title: "", delivery_date: "", currency: "INR", notes: "" });
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);

  const load = async () => { const { data } = await api.get("/procurement/purchase-orders"); setItems(data); };
  useEffect(() => {
    load();
    Promise.all([
      api.get("/vendor/catalog").catch(() => ({ data: [] })),
      api.get("/org/finished-products").catch(() => ({ data: [] })),
    ]).then(([c, f]) => {
      const merged = [...(c.data || []), ...(f.data || [])];
      const seen = new Set();
      setProducts(merged.filter((p) => p.id && !seen.has(p.id) && seen.add(p.id)));
    });
  }, []);

  const ack = async (id) => {
    try { await api.post(`/procurement/purchase-orders/${id}/acknowledge`); toast.success("PO acknowledged"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setLine = (ix, patch) => setLines((prev) => prev.map((l, i) => (i === ix ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (ix) => setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== ix) : prev));

  const pickProduct = (ix, val) => {
    if (val === "__other__") {
      setLine(ix, { product_id: "__other__", product_name: "" });
      return;
    }
    const p = products.find((x) => x.id === val);
    setLine(ix, { product_id: val, product_name: p ? [p.name, p.size || p.tile_size, p.finish].filter(Boolean).join(" · ") : "" });
  };

  const total = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.rate) || 0), 0),
    [lines]
  );

  const resetForm = () => {
    setForm({ buyer_name: "", title: "", delivery_date: "", currency: "INR", notes: "" });
    setLines([{ ...EMPTY_LINE }]);
  };

  const submit = async () => {
    if (!form.buyer_name.trim()) return toast.error("Buyer name is required.");
    const validLines = lines.filter((l) => l.product_name.trim());
    if (validLines.length === 0) return toast.error("Add at least one product (select or type a name).");
    if (total <= 0) return toast.error("Enter quantity and rate — total must be greater than 0.");
    setSaving(true);
    try {
      await api.post("/vendor/purchase-orders", {
        buyer_name: form.buyer_name.trim(),
        title: form.title.trim() || validLines.map((l) => l.product_name).join(", ").slice(0, 120),
        total,
        delivery_date: form.delivery_date || null,
        currency: form.currency || "INR",
        notes: form.notes || null,
        lines: validLines.map((l) => ({
          product_id: l.product_id && l.product_id !== "__other__" ? l.product_id : null,
          product_name: l.product_name.trim(),
          quantity: Number(l.quantity) || 0,
          rate: Number(l.rate) || 0,
          amount: (Number(l.quantity) || 0) * (Number(l.rate) || 0),
        })),
      });
      toast.success("PO recorded");
      setOpen(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="vendor-po-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Purchase orders</h1>
          <p className="text-muted-foreground">Approved POs from your buyers plus any you record manually.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="vendor-po-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Add PO
        </Button>
      </div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Number</TableHead>
            <TableHead>Buyer</TableHead>
            <TableHead>Title / Products</TableHead>
            <TableHead>Total</TableHead>
            <TableHead>Delivery</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`vendor-po-${i.id}`}>
                <TableCell className="font-mono text-xs">{i.number}</TableCell>
                <TableCell className="text-xs">{i.buyer_name || i.buyer_email || "—"}{i.manual && <Badge className="ml-2 text-[9px] bg-orange-500 hover:bg-orange-500">Manual</Badge>}</TableCell>
                <TableCell>
                  <div className="font-medium">{i.title}</div>
                  {(i.lines || []).length > 0 && (
                    <div className="text-[10px] text-muted-foreground truncate max-w-[280px]">
                      {(i.lines || []).map((l) => `${l.product_name || l.item_name || ""}${l.quantity ? ` ×${l.quantity}` : ""}`).filter(Boolean).join(", ")}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-mono">{i.currency} {Number(i.total || 0).toLocaleString()}</TableCell>
                <TableCell className="text-xs">{i.delivery_date || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{i.status}</Badge></TableCell>
                <TableCell>{i.status === "APPROVED" && !i.manual && <Button size="sm" onClick={() => ack(i.id)} data-testid={`vendor-po-ack-${i.id}`}>Acknowledge</Button>}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No purchase orders yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vendor-po-add-dialog">
          <DialogHeader>
            <DialogTitle>Record a purchase order</DialogTitle>
            <DialogDescription>Log a PO you received from a buyer offline (phone, WhatsApp, email). It shows up in your list with a Manual badge.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1"><Label className="text-xs">Buyer name *</Label>
              <Input value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} placeholder="e.g. Stonewave Buyers Pvt Ltd" data-testid="vendor-po-buyer-input" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Products *</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="vendor-po-add-line-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add product
                </Button>
              </div>
              {lines.map((l, ix) => (
                <div key={ix} className="grid grid-cols-12 gap-2 items-end rounded-lg border border-border p-2" data-testid={`vendor-po-line-${ix}`}>
                  <div className="col-span-12 sm:col-span-5 space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Product</Label>
                    <Select value={l.product_id} onValueChange={(v) => pickProduct(ix, v)}>
                      <SelectTrigger data-testid={`vendor-po-line-${ix}-product-select`}><SelectValue placeholder="Select product…" /></SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{[p.name, p.size || p.tile_size, p.finish].filter(Boolean).join(" · ")}</SelectItem>
                        ))}
                        <SelectItem value="__other__">Other (type name below)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input value={l.product_name} onChange={(e) => setLine(ix, { product_name: e.target.value })}
                           placeholder="Product name" className="mt-1" data-testid={`vendor-po-line-${ix}-name-input`} />
                  </div>
                  <div className="col-span-4 sm:col-span-2 space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Qty (boxes)</Label>
                    <Input type="number" min="0" value={l.quantity} onChange={(e) => setLine(ix, { quantity: e.target.value })} data-testid={`vendor-po-line-${ix}-qty-input`} />
                  </div>
                  <div className="col-span-4 sm:col-span-2 space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Rate (₹)</Label>
                    <Input type="number" min="0" step="0.01" value={l.rate} onChange={(e) => setLine(ix, { rate: e.target.value })} data-testid={`vendor-po-line-${ix}-rate-input`} />
                  </div>
                  <div className="col-span-3 sm:col-span-2 space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Amount</Label>
                    <div className="h-9 flex items-center font-mono text-sm">₹{((Number(l.quantity) || 0) * (Number(l.rate) || 0)).toLocaleString()}</div>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeLine(ix)} disabled={lines.length === 1} data-testid={`vendor-po-line-${ix}-remove-btn`}>
                      <Trash2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="flex justify-end items-center gap-2 pr-1">
                <span className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Total</span>
                <span className="font-display font-black text-lg" data-testid="vendor-po-total-computed">₹{total.toLocaleString()}</span>
              </div>
            </div>

            <div className="space-y-1"><Label className="text-xs">Title (optional — auto-filled from products)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. 600x600 Glossy Ivory — 100 boxes" data-testid="vendor-po-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Delivery date</Label>
                <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} data-testid="vendor-po-date-input" />
              </div>
              <div className="space-y-1"><Label className="text-xs">Currency</Label>
                <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="vendor-po-currency-input" />
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Special instructions, transport, discount…" data-testid="vendor-po-notes-input" />
            </div>
            <Button onClick={submit} disabled={saving} className="w-full" data-testid="vendor-po-submit-btn">
              {saving ? "Saving…" : "Record PO"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
